import { useCallback, useEffect, useState } from 'react';
import { Factory, LayoutGrid, Loader2, MapPin, Package, Star, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api, {
  getCrmOrderBoardCard,
  patchCrmOrderSalesStage,
  postCrmOrderActivity,
  type CrmOrderBoardCard,
} from '../../../api';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { AdminGlassDisclosure } from '../AdminGlassDisclosure';
import {
  type ItemDraft,
  type NewItemDraft,
} from '../../../pages/admin/adminOrdersShared';
import { OrderLineSuggestInput } from './OrderLineSuggestInput';
import { openOrderComposeCatalog } from '../../../orderComposeStore';
import type { CrmCatalogSuggestItem } from '../../../api';
import { CrmStageNotifySheet } from './CrmStageNotifySheet';

const ORDER_NOTE_TEMPLATES = [
  'Заказ подтвержден. Передали в производство.',
  'Согласуем удобную дату монтажа. Напишем сегодня.',
];

type Props = {
  orderId: number;
  orderNumber: string;
  canEditPricing: boolean;
  canEditItems: boolean;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onUpdated: () => void;
  onOpenCatalog?: () => void;
  onCloseDeal?: () => void;
  onProductionHandoff?: () => void;
  salesStage?: string | null;
  hasClientTelegram?: boolean;
  clientLabel?: string;
};

export function CrmOrderComposePanel({
  orderId,
  orderNumber,
  canEditPricing,
  canEditItems,
  hapticSelection,
  hapticSuccess,
  onUpdated,
  onOpenCatalog,
  onCloseDeal,
  onProductionHandoff,
  salesStage,
  hasClientTelegram = true,
  clientLabel,
}: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<CrmOrderBoardCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [discountPct, setDiscountPct] = useState(0);
  const [managerNote, setManagerNote] = useState('');
  const [itemDrafts, setItemDrafts] = useState<Record<number, ItemDraft>>({});
  const [newItem, setNewItem] = useState<NewItemDraft>({ name: '', quantity: 1, unit_price: 0 });
  const [catalogHint, setCatalogHint] = useState<string | null>(null);
  const [handoffDone, setHandoffDone] = useState(false);
  const [productionNotifyOpen, setProductionNotifyOpen] = useState(false);
  const alreadyInProduction = salesStage === 'production' || handoffDone;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const card = await getCrmOrderBoardCard(orderId);
      setData(card);
      setAddress(card.order.delivery_address || '');
      setDiscountPct(card.order.discount_pct);
      setManagerNote(card.order.manager_notes || '');
      const drafts: Record<number, ItemDraft> = {};
      for (const row of card.line_items) {
        drafts[row.id] = { quantity: row.quantity, unit_price: row.unit_price };
      }
      setItemDrafts(drafts);
    } catch (err: unknown) {
      setError(formatAdminPanelError(err, 'Не удалось загрузить состав'));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      hapticSuccess();
      await load();
      onUpdated();
    } catch (err: unknown) {
      setError(formatAdminPanelError(err, 'Ошибка сохранения'));
    } finally {
      setBusy(false);
    }
  };

  const saveAddress = () =>
    run(async () => {
      await api.patch(`/admin/crm/orders/${orderId}/delivery`, { delivery_address: address.trim() || null });
    });

  const applyPricing = () =>
    run(async () => {
      await api.patch(`/admin/crm/orders/${orderId}/pricing`, {
        discount_pct: discountPct,
        manager_notes: managerNote.trim() || undefined,
      });
    });

  const saveItem = (itemId: number) =>
    run(async () => {
      const draft = itemDrafts[itemId];
      if (!draft) return;
      await api.patch(`/admin/orders/${orderId}/items/${itemId}`, {
        quantity: draft.quantity,
        unit_price: draft.unit_price,
      });
    });

  const removeItem = (itemId: number) =>
    run(async () => {
      await api.delete(`/admin/orders/${orderId}/items/${itemId}`);
    });

  const addItem = () =>
    run(async () => {
      if (!newItem.name.trim()) {
        setError('Укажите название позиции');
        return;
      }
      await api.post(`/admin/orders/${orderId}/items`, {
        name: newItem.name.trim(),
        quantity: Math.max(1, Number(newItem.quantity || 1)),
        unit_price: Math.max(0, Number(newItem.unit_price || 0)),
        product_id: newItem.product_id,
      });
      setNewItem({ name: '', quantity: 1, unit_price: 0 });
      setCatalogHint(null);
    });

  const confirmSendToProduction = (opts: { notifyClient: boolean; clientMessage: string }) =>
    run(async () => {
      setProductionNotifyOpen(false);
      if (managerNote.trim()) {
        await api.patch(`/admin/crm/orders/${orderId}/pricing`, {
          manager_notes: managerNote.trim(),
        });
      }
      await patchCrmOrderSalesStage(orderId, 'production', {
        force: true,
        notifyClient: opts.notifyClient,
        clientMessage: opts.clientMessage,
      });
      await postCrmOrderActivity(orderId, {
        body: 'Состав согласован — карточка в колонке «Производство».',
        kind: 'handoff_to_production',
      });
      setHandoffDone(true);
      hapticSuccess();
      onProductionHandoff?.();
    });

  const requestSendToProduction = () => {
    hapticSelection();
    setProductionNotifyOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--cyan)' }} />
      </div>
    );
  }

  if (!data) {
    return <p className="text-xs" style={{ color: '#FC814A' }}>{error || 'Нет данных'}</p>;
  }

  const { order, line_items, order_has_pricing_drift } = data;

  return (
    <div className="space-y-2">
      <div
        className="rounded-xl px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-[11px]"
        style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid rgba(99,179,237,0.2)' }}
      >
        <span style={{ color: 'var(--text-2)' }}>
          Итого: <strong style={{ color: 'var(--cyan-bright)' }}>{Math.round(order.final_amount).toLocaleString('ru-RU')} ₽</strong>
          {order.discount_pct > 0 && ` · скидка ${order.discount_pct}%`}
        </span>
        <span style={{ color: 'var(--text-3)' }}>{line_items.length} поз.</span>
      </div>

      <AdminGlassDisclosure
        accent="slate"
        density="compact"
        defaultOpen={false}
        title="Адрес и примечание"
        subtitle="Доставка / монтаж"
        icon={<MapPin size={14} color="var(--text-2)" />}
      >
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          disabled={!canEditItems || busy}
          placeholder="Город, улица, подъезд…"
          className="input-glass w-full px-3 py-2 rounded-xl text-xs mb-2"
        />
        <textarea
          rows={2}
          value={managerNote}
          onChange={(e) => setManagerNote(e.target.value)}
          disabled={!canEditPricing || busy}
          placeholder="Комментарий клиенту (статус, согласование)"
          className="input-glass w-full text-xs rounded-xl px-3 py-2 resize-none"
        />
        {canEditItems && (
          <button
            type="button"
            disabled={busy}
            onClick={() => { hapticSelection(); void saveAddress(); }}
            className="press mt-2 w-full py-2 rounded-xl text-[11px] font-bold disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
          >
            Сохранить адрес
          </button>
        )}
      </AdminGlassDisclosure>

      <AdminGlassDisclosure
        accent="cyan"
        density="compact"
        defaultOpen={false}
        title="Позиции заказа"
        subtitle="Добавить, изменить цену и количество"
        badge={order_has_pricing_drift ? 'цены' : line_items.length}
        icon={<Package size={14} color="#63B3ED" />}
      >
        {line_items.length === 0 ? (
          <p className="text-[10px] mb-2" style={{ color: 'var(--text-3)' }}>
            Позиций пока нет — добавьте строки по результатам замера (карниз, ткань, монтаж).
          </p>
        ) : (
          <div className="space-y-2 mb-2">
            {line_items.map((item) => {
              const displayTitle = item.name;
              const cfgLines = item.detail_lines;
              const draft = itemDrafts[item.id] ?? { quantity: item.quantity, unit_price: item.unit_price };
              return (
                <div
                  key={item.id}
                  className="rounded-xl p-2.5"
                  style={{ border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.03)' }}
                >
                  <p className="text-xs font-semibold mb-1 leading-snug" style={{ color: 'var(--text-1)' }}>
                    {displayTitle}
                  </p>
                  {cfgLines.map((line) => (
                    <p key={line} className="text-[10px]" style={{ color: 'var(--text-3)' }}>{line}</p>
                  ))}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <p className="text-[10px]" style={{ color: 'var(--cyan)' }}>
                      Сумма строки: {item.total_price.toLocaleString('ru-RU')} ₽
                    </p>
                    {item.pricing_changed && (
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(246,173,85,0.2)', color: '#F6AD55' }}
                      >
                        цена в каталоге изменилась
                      </span>
                    )}
                  </div>
                  {canEditItems && (
                    <>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <input
                          type="number"
                          min={1}
                          value={draft.quantity}
                          disabled={busy}
                          onChange={(e) =>
                            setItemDrafts((prev) => ({
                              ...prev,
                              [item.id]: { ...draft, quantity: Number(e.target.value || 1) },
                            }))
                          }
                          className="input-glass w-full px-2 py-1.5 rounded-lg text-xs"
                          placeholder="Кол-во"
                        />
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={draft.unit_price}
                          disabled={busy}
                          onChange={(e) =>
                            setItemDrafts((prev) => ({
                              ...prev,
                              [item.id]: { ...draft, unit_price: Number(e.target.value || 0) },
                            }))
                          }
                          className="input-glass w-full px-2 py-1.5 rounded-lg text-xs"
                          placeholder="Цена"
                        />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => { hapticSelection(); void saveItem(item.id); }}
                          className="press flex-1 py-1.5 rounded-lg text-[10px] font-bold disabled:opacity-50"
                          style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                        >
                          Сохранить
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => { hapticSelection(); void removeItem(item.id); }}
                          className="press px-2.5 py-1.5 rounded-lg disabled:opacity-50"
                          style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {canEditItems && (
          <div className="rounded-xl p-2.5" style={{ border: '1px dashed var(--border-glass)', background: 'rgba(255,255,255,0.02)' }}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--cyan)' }}>+ Позиция</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  hapticSelection();
                  const target = { orderId, orderNumber };
                  if (onOpenCatalog) {
                    onOpenCatalog();
                    return;
                  }
                  openOrderComposeCatalog(navigate, target, onCloseDeal);
                }}
                className="press flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold disabled:opacity-50 shrink-0"
                style={{ background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
              >
                <LayoutGrid size={12} />
                Каталог
              </button>
            </div>
            <p className="text-[10px] mb-2 leading-snug" style={{ color: 'var(--text-3)' }}>
              Точный расчёт — через Каталог и конфигуратор (как у клиента). Поиск здесь — по базе товаров и систем v2;
              цена «от …» из матрицы прайса, не полный калькулятор.
            </p>
            <OrderLineSuggestInput
              value={newItem.name}
              disabled={busy}
              placeholder="Название из каталога…"
              onChange={(name) => setNewItem((p) => ({ ...p, name, product_id: undefined }))}
              onPick={(s: CrmCatalogSuggestItem) => {
                if (s.kind === 'catalog_system' && s.system_slug) {
                  setCatalogHint('Для системы откройте каталог и настройте размеры в конфигураторе.');
                  const target = { orderId, orderNumber };
                  if (onOpenCatalog) {
                    onOpenCatalog();
                    return;
                  }
                  openOrderComposeCatalog(navigate, target, onCloseDeal);
                  return;
                }
                setCatalogHint(null);
                setNewItem({
                  name: s.label,
                  quantity: 1,
                  unit_price: s.unit_price > 0 ? s.unit_price : 0,
                  product_id: s.product_id,
                });
              }}
            />
            {catalogHint && (
              <p className="text-[10px] mt-1.5" style={{ color: '#F6AD55' }}>{catalogHint}</p>
            )}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="number"
                min={1}
                value={newItem.quantity}
                disabled={busy}
                onChange={(e) => setNewItem((p) => ({ ...p, quantity: Number(e.target.value || 1) }))}
                className="input-glass w-full px-2 py-1.5 rounded-lg text-xs"
                placeholder="Кол-во"
              />
              <input
                type="number"
                min={0}
                step="0.01"
                value={newItem.unit_price}
                disabled={busy}
                onChange={(e) => setNewItem((p) => ({ ...p, unit_price: Number(e.target.value || 0) }))}
                className="input-glass w-full px-2 py-1.5 rounded-lg text-xs"
                placeholder="Цена"
              />
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => { hapticSelection(); void addItem(); }}
              className="press w-full py-2 rounded-lg text-[11px] font-bold disabled:opacity-50"
              style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}
            >
              Добавить в заказ
            </button>
          </div>
        )}
      </AdminGlassDisclosure>

      {canEditPricing && (
        <AdminGlassDisclosure
          accent="coral"
          density="compact"
          defaultOpen={false}
          title="Скидка и итог"
          subtitle="Пересчёт после правок позиций"
          icon={<Star size={14} color="#FC814A" />}
        >
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={discountPct}
              disabled={busy}
              onChange={(e) => setDiscountPct(Number(e.target.value || 0))}
              className="input-glass flex-1 px-3 py-2 rounded-xl text-sm"
            />
            <span className="text-xs shrink-0" style={{ color: 'var(--text-3)' }}>%</span>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => { hapticSelection(); void applyPricing(); }}
            className="press mt-2 w-full py-2 rounded-xl text-[11px] font-bold disabled:opacity-50"
            style={{ background: 'rgba(99,179,237,0.15)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
          >
            Применить скидку и комментарий
          </button>
          <div className="flex gap-1.5 flex-wrap mt-2">
            {ORDER_NOTE_TEMPLATES.map((tpl) => (
              <button
                key={tpl}
                type="button"
                onClick={() => setManagerNote(tpl)}
                className="press px-2 py-1 rounded-lg text-[10px] font-semibold"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }}
              >
                {tpl.slice(0, 28)}…
              </button>
            ))}
          </div>
        </AdminGlassDisclosure>
      )}

      {handoffDone && (
        <p
          className="text-[11px] px-3 py-2 rounded-xl font-semibold"
          style={{ background: 'rgba(72,187,120,0.12)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}
        >
          Передано в производство — карточка в соответствующей колонке воронки, статус заказа обновлён.
        </p>
      )}

      {canEditItems && line_items.length > 0 && (
        <button
          type="button"
          disabled={busy || alreadyInProduction}
          onClick={() => void requestSendToProduction()}
          className="press w-full flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-bold disabled:opacity-50 min-h-[48px]"
          style={{
            background: alreadyInProduction ? 'rgba(255,255,255,0.06)' : 'rgba(159,122,234,0.2)',
            color: alreadyInProduction ? 'var(--text-3)' : '#D6BCFA',
            border: `1px solid ${alreadyInProduction ? 'var(--border-subtle)' : 'rgba(159,122,234,0.4)'}`,
          }}
        >
          <Factory size={16} />
          {busy
            ? 'Сохраняем…'
            : alreadyInProduction
              ? 'Уже в производстве'
              : 'Согласовано → передать в производство'}
        </button>
      )}

      {error && <p className="text-[10px]" style={{ color: '#FC814A' }}>{error}</p>}

      {productionNotifyOpen && (
        <CrmStageNotifySheet
          orderNumber={orderNumber}
          stage="production"
          hasTelegram={hasClientTelegram}
          clientLabel={clientLabel}
          onCancel={() => setProductionNotifyOpen(false)}
          onConfirm={(opts) => {
            void confirmSendToProduction(opts);
          }}
        />
      )}
    </div>
  );
}
